import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MissionMarker, VehicleMarker } from '../../../core/models/Marker';
import { TranslocoPipe } from '@ngneat/transloco';
import { DatetimePipe } from '../../../shared/pipes/datetime.pipe';
import L, { LatLng, latLng, tileLayer } from 'leaflet';

@Component({
  selector: 'app-mission-map',
  template: `<div id="map" [style.height]="mapHeight"></div>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapComponent implements OnChanges, AfterViewInit {
  @Input() missionMarkers: MissionMarker[] = [];
  @Input() vehicleMarkers: VehicleMarker[] = [];
  @Input() showRoutes = false;
  @Input() mapHeight = '90vh';

  map: L.Map;
  markers: L.Popup[] = [];
  routes: L.GeoJSON[] = [];

  constructor(private translocoPipe: TranslocoPipe, private datetimePipe: DatetimePipe) {
  }

  ngAfterViewInit() {
    this.initMap();
    this.render();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.missionMarkers?.isFirstChange()) {
      this.render();
    }
  }

  private initMap(): void {
    const configuration = {
      layers: [
        tileLayer(`https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`, {
          minZoom: 5,
          maxZoom: 18,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          referrerPolicy: 'origin-when-cross-origin',
        }),
      ],
      zoom: 13,
      center: latLng(48.137154, 11.576124),
    };

    this.map = L.map('map', configuration);
  }

  private render() {
    this.manageMarkers();
    this.manageRoutes();
    this.manageViewport();
  }

  private manageMarkers(): void {
    this.markers?.forEach((marker) => marker.remove());
    this.markers = [];
    this.missionMarkers?.forEach((missionMarker) => this.markers.push(this.getMissionMarker(missionMarker)));
    this.vehicleMarkers?.forEach((vehicleMarker) => this.markers.push(this.getVehicleMarker(vehicleMarker)));
  }

  private manageViewport(): void {
    if (!this.markers.length) {
      this.map.setZoom(3);
      return;
    }

    if (this.markers.length === 1) {
      this.map.flyTo(this.markers[0].getLatLng());
      this.map.setZoom(7);
      return;
    }

    const bounds: LatLng[] = [];
    this.markers.forEach((marker: L.Popup) => bounds.push(marker.getLatLng()));
    this.vehicleMarkers?.forEach((vehicleMarker) => {
      vehicleMarker.route?.itinerary?.forEach((point) => bounds.push(L.latLng(point.latitude, point.longitude)));
    });
    this.map.fitBounds(L.latLngBounds(bounds), { duration: 0, padding: [80, 80] });
  }

  private manageRoutes(): void {
    if (!this.showRoutes) {
      return;
    }

    this.vehicleMarkers.forEach((vehicleMarker) => {
      const route = L.geoJSON(vehicleMarker.route.routeGeoJson, {
        style: () => {
          return { stroke: true, color: 'black' };
        }
      }).addTo(this.map);

      this.routes.push(route);
    });
  }

  private getMissionMarker(marker: MissionMarker): L.Popup {
    const iconElement = document.createElement('div');
    iconElement.className = `icon icon-mission-${marker.type.toLowerCase()}-circle-no-bg`;

    const descriptionElement = document.createElement('div');
    descriptionElement.textContent = marker.name;
    descriptionElement.className = `mission-marker-description`;

    const missionElement = document.createElement('div');
    missionElement.className = `d-flex align-items-center column-gap-1 bg-white rounded`;
    missionElement.appendChild(iconElement);
    missionElement.appendChild(descriptionElement);

    const rootElement = document.createElement('div');
    rootElement.className = `d-flex flex-column align-items-center`;
    rootElement.appendChild(missionElement);

    return L.popup({ closeButton: false, closeOnClick: false, autoClose: false })
      .setLatLng([marker.lat, marker.lng])
      .setContent(rootElement)
      .openOn(this.map);
  }

  private getVehicleMarker(marker: VehicleMarker): L.Popup {
    const iconElement = document.createElement('i');
    iconElement.className = `icon icon-vehicle-${marker.etaStatus?.toLowerCase()}-circle`;

    const infosContainer = document.createElement('div');
    infosContainer.className = 'content';

    const transport = document.createElement('span');
    transport.textContent = `${marker.transportNumber}`;
    transport.className = `text-truncate`;

    const eta = document.createElement('span');
    eta.className = 'font-weight-bold text-truncate';
    const etaValue = marker.eta
      ? this.datetimePipe.transform(marker.eta, { withTimezone: false, onlyTimeForToday: true, withYear: false })
      : 'N/A';
    eta.textContent = `${this.translocoPipe.transform('mission.eta')} ${etaValue}`;

    infosContainer.appendChild(transport);
    infosContainer.appendChild(eta);

    const container = document.createElement('div');
    container.className = 'truck-badge';
    container.appendChild(iconElement);
    container.appendChild(infosContainer);

    return L.popup({ closeButton: false, closeOnClick: false, autoClose: false })
      .setLatLng([marker.lat, marker.lng])
      .setContent(container)
      .openOn(this.map);
  }
}
